/**
 * A helper class to help components deal with plot data formatting and manipulation.
 */
export default class PlotUtils {
  /**
   * Transforms plot data from the API into the format required by the Plotbox component.
   * @param {Object} plot - The plot data object from the backend
   * @param {string} plot.yName - The name of the y-axis data
   * @param {Array[]} plot.yData - Array of y-axis data series
   * @param {Array} plot.sliderData - Array of slider values corresponding to each series
   * @param {string} plot.sliderName - Name of the swept variable
   * @param {Array[]} plot.xData - Array of x-axis data
   * @param {string} plot.xName - The name of the x-axis
   * @param {string} plot.xUnit - Unit of the x-axis
   * @param {string} plot.yUnit - Unit of the y-axis
   * @param {number} plot.id - Unique identifier for the plot
   * @param {boolean} showDiffractionLimit - Flag to control the visibility of the diffraction limit trace
   * @returns {Object} Data formatted for Plotbox component
   */
  static formatDataForPlotbox = (
    plot,
    showDiffractionLimit,
    sweptVariables
  ) => {
    if (!plot || !plot.yData || !Array.isArray(plot.yData)) {
      return {
        series: [],
        isMtfPlot: true,
        plotType: "2D"
      };
    }

    const isTangentialSagittal =
      plot.yName && plot.yName.includes("Tangential/Sagittal");
    let series = [];

    if (isTangentialSagittal) {
      // For Tangential/Sagittal plots, we need to reorganize the data
      // Backend sends: [dz724_T, dz725_T, dz724_S, dz725_S, DL_T, DL_S]
      // We want legend order: [dz724_T, dz725_T, dz724_S, dz725_S, DL_T, DL_S]

      // Separate diffraction limits from regular data
      const regularSeriesCount = plot.sliderData.filter(
        val =>
          val !== null &&
          val !== undefined &&
          !(typeof val === "string" && val.startsWith("Diff. Limit"))
      ).length;

      // Process regular series
      const tangentialSeries = [];
      const sagittalSeries = [];

      for (let i = 0; i < regularSeriesCount; i++) {
        const sweepValue = plot.sliderData[i];
        const xData = plot.xData[i] || plot.xData[0];
        const ySeries = plot.yData[i];

        const baseName = plot.sliderName
          ? `${plot.sliderName} = ${sweepValue}`
          : sweepValue;

        // Assuming the backend sends tangential first, then sagittal
        // If regularSeriesCount is 4 and we have 2 sweep values, then:
        // indices 0,1 are tangential for each sweep value
        // indices 2,3 are sagittal for each sweep value
        const halfPoint = regularSeriesCount / 2;

        if (i < halfPoint) {
          // Tangential series
          tangentialSeries.push({
            name:
              sweptVariables && sweptVariables.length > 0
                ? `${baseName} (T)`
                : baseName,
            values: ySeries,
            xData: xData
          });
        } else {
          // Sagittal series
          sagittalSeries.push({
            name:
              sweptVariables && sweptVariables.length > 0
                ? `${baseName} (S)`
                : baseName,
            values: ySeries,
            xData: xData
          });
        }
      }

      // Add all tangential series first, then all sagittal series
      series = [...tangentialSeries, ...sagittalSeries];

      // Process diffraction limit series if they exist
      for (let i = regularSeriesCount; i < plot.yData.length; i++) {
        const sweepValue = plot.sliderData[i];
        const xData = plot.xData[i] || plot.xData[0];
        const ySeries = plot.yData[i];

        // Determine if this is tangential or sagittal diffraction limit
        const dlIndex = i - regularSeriesCount;
        const isDlTangential = dlIndex === 0;

        series.push({
          name: isDlTangential
            ? "Diffraction Limit (T)"
            : "Diffraction Limit (S)",
          values: ySeries,
          xData: xData,
          line: { dash: "dash", color: isDlTangential ? "black" : "#666666" }
        });
      }
    } else {
      // For non-Tangential/Sagittal plots (single direction), use original logic
      series = plot.yData.map((ySeries, index) => {
        const sweepValue = plot.sliderData[index];
        const xData = plot.xData[index] || plot.xData[0];
        let seriesName;
        let seriesOptions = {};

        const isDiffractionLimit =
          sweepValue === null ||
          sweepValue === undefined ||
          (typeof sweepValue === "string" &&
            sweepValue.startsWith("Diff. Limit"));

        if (isDiffractionLimit) {
          seriesName = sweepValue || "Diffraction Limit";
          seriesOptions.line = { dash: "dash", color: "black" };
        } else {
          seriesName = plot.sliderName
            ? `${plot.sliderName} = ${sweepValue}`
            : sweepValue;
        }

        return {
          name: seriesName,
          values: ySeries,
          xData: xData,
          sliderData: plot.sliderData || [],
          sliderName: plot.sliderName || "",
          ...seriesOptions
        };
      });
    }

    // Filter out diffraction limit if needed
    if (!showDiffractionLimit) {
      series = PlotUtils.filterDiffractionLimit(series);
    }

    const allX = [];
    const allY = [];
    series.forEach(s => {
      if (Array.isArray(s.xData)) {
        allX.push(...s.xData);
      }
      if (Array.isArray(s.values)) {
        allY.push(...s.values);
      }
    });

    // Compute min/max for x and y
    const xAxisRange =
      allX.length > 0 ? [Math.min(...allX), Math.max(...allX)] : [0, 1];
    const yAxisRange =
      allY.length > 0 ? [Math.min(...allY), Math.max(...allY)] : [0, 1];

    return {
      isMtfPlot: true,
      plotType: "2D",
      id: plot.id,
      series: series,
      xName: plot.xName || "",
      xUnit: plot.xUnit || "",
      yName: plot.yName || "",
      yUnit: plot.yUnit || "",
      zData: null,
      xAxisRange: xAxisRange,
      yAxisRange: yAxisRange,
      sliderName: plot.sliderName || "",
      sliderData: plot.sliderData || []
    };
  };

  /**
   * Uses data from the plot and formattedData to create a plotbox
   * that can be used for the CSV export since the rest of the MTF plot has a different format
   * @param {Object} plot - The plot data object
   * @param {Object} formattedData - The formatted plotbox data returned by formatDataForPlotbox
   * @returns {Object} plotbox data formatted for plotactions component
   */
  static getPlotboxFromFormattedData = (plot, formattedData) => {
    let plotboxes = [];

    for (const series of formattedData.series) {
      let plotbox = {
        id: plot.id,
        xData: series.xData,
        yData: [series.values],
        type: "2D",
        plotType: "2D",
        sliderData: [[series.name]],
        xName: formattedData.xName,
        yName: formattedData.yName,
        sliderName: formattedData.sliderName || "Type"
      };
      plotboxes.push(plotbox);
    }

    return plotboxes;
  };

  /**
   * Validates if a plot object has the required structure for formatting.
   * @param {Object} plot - The plot data object to validate
   * @returns {boolean} True if the plot object is valid, false otherwise
   */
  static isValidPlotData = plot => {
    if (!plot || typeof plot !== "object") {
      return false;
    }

    const requiredFields = ["yData", "sliderData", "xData"];
    return requiredFields.every(
      field => plot.hasOwnProperty(field) && Array.isArray(plot[field])
    );
  };

  /**
   * Extracts series names from plot data based on slider values and plot type.
   * @param {Object} plot - The plot data object
   * @returns {string[]} Array of series names
   */
  static getSeriesNames = plot => {
    if (!PlotUtils.isValidPlotData(plot)) {
      return [];
    }

    const isTangentialSagittal =
      plot.yName && plot.yName.includes("Tangential/Sagittal");

    if (isTangentialSagittal) {
      // For T/S plots, reorganize the names properly
      const names = [];
      const regularSeriesCount = plot.sliderData.filter(
        val =>
          val !== null &&
          val !== undefined &&
          !(typeof val === "string" && val.startsWith("Diff. Limit"))
      ).length;

      const halfPoint = regularSeriesCount / 2;

      // Add tangential names
      for (let i = 0; i < halfPoint; i++) {
        const sweepValue = plot.sliderData[i];
        names.push(`${plot.sliderName} = ${sweepValue} (T)`);
      }

      // Add sagittal names
      for (let i = halfPoint; i < regularSeriesCount; i++) {
        const sweepValue = plot.sliderData[i];
        names.push(`${plot.sliderName} = ${sweepValue} (S)`);
      }

      // Add diffraction limit names
      for (let i = regularSeriesCount; i < plot.yData.length; i++) {
        const dlIndex = i - regularSeriesCount;
        names.push(
          dlIndex === 0 ? "Diffraction Limit (T)" : "Diffraction Limit (S)"
        );
      }

      return names;
    } else {
      // Original logic for non-T/S plots
      return plot.yData.map((_, index) => {
        const sweepValue = plot.sliderData[index];

        if (sweepValue === null || sweepValue === undefined) {
          return "Diffraction Limit";
        }

        return `${plot.sliderName} = ${sweepValue}`;
      });
    }
  };

  /**
   * Filters out diffraction limit series from plot data.
   * @param {Array} series - Array of series objects
   * @returns {Array} Filtered series array without diffraction limit
   */
  static filterDiffractionLimit = series => {
    if (!Array.isArray(series)) {
      return [];
    }
    return series.filter(s => !s.name.includes("Diffraction Limit"));
  };

  /**
   * Checks if a plot contains tangential/sagittal data based on the y-axis name.
   * @param {Object} plot - The plot data object
   * @returns {boolean} True if plot contains tangential/sagittal data
   */
  static isTangentialSagittalPlot = plot => {
    if (!plot || typeof plot.yName !== "string") {
      return false;
    }
    return plot.yName.includes("Tangential/Sagittal");
  };

  /**
   * Gets the default line style for diffraction limit series.
   * @returns {Object} Line style configuration for diffraction limit
   */
  static getDiffractionLimitStyle = () => {
    return { dash: "dash", color: "black" };
  };
}
